import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtMoneyShort, fmtNum } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import {
  DollarSign,
  TrendingUp,
  Users,
  Receipt,
  Building2,
  AlertCircle,
  Briefcase,
  ArrowUpRight,
  Clock,
  Activity as ActivityIcon,
  Plus,
  Pencil,
  Trash2,
  Upload as UploadIcon,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StateMap from "@/components/StateMap";
import LatestProduction from "@/components/LatestProduction";

const METRIC_OPTIONS = [
  { value: "reported_gwp", label: "Reported GWP" },
  { value: "broker_commission", label: "Broker Commission" },
  { value: "mga_net_commission_prov", label: "MGA Net (Prov)" },
  { value: "mga_net_commission_exp", label: "MGA Net (Exp)" },
  { value: "ar_outstanding", label: "AR Outstanding" },
  { value: "premium_tax", label: "Premium Tax" },
];

const FIELD_OPTIONS = [
  { value: "business_segment", label: "Business Segment" },
  { value: "carrier", label: "Carrier" },
  { value: "broker", label: "Broker" },
  { value: "line_of_biz", label: "Line of Biz" },
  { value: "state", label: "State" },
  { value: "transaction_code", label: "Transaction Code" },
];

const BAR_COLORS = ["#3B82F6", "#0EA5E9", "#14B8A6", "#6366F1", "#8B5CF6", "#F59E0B", "#10B981", "#EC4899"];

function KpiCard({ icon: Icon, label, value, hint, accent = "blue", testid, onClick }) {
  const tints = {
    blue: { bg: "bg-blue-50", icon: "text-blue-600", ring: "ring-blue-100" },
    teal: { bg: "bg-teal-50", icon: "text-teal-600", ring: "ring-teal-100" },
    amber: { bg: "bg-amber-50", icon: "text-amber-600", ring: "ring-amber-100" },
    rose: { bg: "bg-rose-50", icon: "text-rose-600", ring: "ring-rose-100" },
    indigo: { bg: "bg-indigo-50", icon: "text-indigo-600", ring: "ring-indigo-100" },
    slate: { bg: "bg-slate-100", icon: "text-slate-600", ring: "ring-slate-200" },
  };
  const t = tints[accent] || tints.blue;
  return (
    <Card
      data-testid={testid}
      onClick={onClick}
      className={`p-5 border-slate-200 hover:shadow-sm transition-shadow ${
        onClick ? "cursor-pointer hover:border-blue-300 hover:-translate-y-0.5 transition-transform" : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-lg ${t.bg} flex items-center justify-center ring-1 ${t.ring}`}>
          <Icon className={`w-[18px] h-[18px] ${t.icon}`} strokeWidth={1.75} />
        </div>
        <ArrowUpRight className={`w-4 h-4 ${onClick ? "text-blue-400" : "text-slate-300"}`} />
      </div>
      <div className="mt-4 text-[11px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
      <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-[12px] text-slate-500">{hint}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [series, setSeries] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [metric, setMetric] = useState("reported_gwp");
  const [field, setField] = useState("business_segment");
  const [recent, setRecent] = useState([]);
  const [aging, setAging] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setKpis(r.data)).catch(() => {});
    api.get("/dashboard/ar-aging").then((r) => setAging(r.data)).catch(() => {});
    api.get("/activity", { params: { limit: 15 } }).then((r) => setActivity(r.data)).catch(() => {});
    api
      .get("/policies", { params: { page: 1, page_size: 6, sort: "reported_gwp", sort_dir: "desc" } })
      .then((r) => setRecent(r.data.items))
      .catch(() => {});
  }, []);

  function refreshActivity() {
    api.get("/activity", { params: { limit: 15 } }).then((r) => setActivity(r.data)).catch(() => {});
  }

  useEffect(() => {
    api
      .get("/dashboard/timeseries", { params: { metric, bucket: "acct_month" } })
      .then((r) => setSeries(r.data))
      .catch(() => {});
  }, [metric]);

  useEffect(() => {
    api
      .get("/dashboard/breakdown", { params: { field, metric } })
      .then((r) => setBreakdown(r.data.slice(0, 8)))
      .catch(() => {});
  }, [field, metric]);

  return (
    <div data-testid="dashboard-page" className="space-y-8 max-w-[1400px]">
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-policies"
          icon={Briefcase}
          label="Policies on file"
          value={kpis ? fmtNum(kpis.policies) : "—"}
          hint={`${kpis?.segments ?? 0} segments • ${kpis?.carriers ?? 0} carriers`}
          accent="blue"
        />
        <KpiCard
          testid="kpi-gwp"
          icon={DollarSign}
          label="Reported GWP (ITD)"
          value={kpis ? fmtMoney(kpis.gwp) : "—"}
          hint="Inception-to-date written premium"
          accent="teal"
        />
        <KpiCard
          testid="kpi-mga-net"
          icon={TrendingUp}
          label="MGA Net Commission (Prov)"
          value={kpis ? fmtMoney(kpis.mga_net_prov) : "—"}
          hint={`Exp: ${kpis ? fmtMoneyShort(kpis.mga_net_exp) : "—"}`}
          accent="indigo"
        />
        <KpiCard
          testid="kpi-ar"
          icon={AlertCircle}
          label="AR Outstanding"
          value={kpis ? fmtMoney(kpis.ar_outstanding) : "—"}
          hint="Click to view open policies"
          accent="amber"
          onClick={() => navigate("/policies?ar=outstanding")}
        />
      </div>

      {/* Trend + Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
                Production trend
              </div>
              <h2 className="text-base font-semibold tracking-tight">By accounting month</h2>
            </div>
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger className="w-[200px] h-9" data-testid="trend-metric-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRIC_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={series.map((d) => ({ name: typeof d.label === "string" ? d.label : "", value: d.value }))}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
                <YAxis
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  stroke="#cbd5e1"
                  tickFormatter={(v) => fmtMoneyShort(v)}
                />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                  formatter={(v) => fmtMoney(v)}
                />
                <Area type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
                Breakdown
              </div>
              <h2 className="text-base font-semibold tracking-tight">Top contributors</h2>
            </div>
            <Select value={field} onValueChange={setField}>
              <SelectTrigger className="w-[140px] h-9" data-testid="breakdown-field-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-3">
            {breakdown.length === 0 && (
              <div className="text-sm text-slate-400 py-8 text-center">No data yet</div>
            )}
            {breakdown.map((row, i) => {
              const max = breakdown[0]?.value || 1;
              const pct = Math.max(2, Math.round((row.value / max) * 100));
              return (
                <div key={i} className="space-y-1">
                  <div className="flex items-center justify-between text-[12px]">
                    <div className="text-slate-700 truncate max-w-[60%]" title={row.label}>{row.label}</div>
                    <div className="font-medium text-slate-900">{fmtMoneyShort(row.value)}</div>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${pct}%`, background: BAR_COLORS[i % BAR_COLORS.length] }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* AR Aging */}
      <Card className="p-6 border-slate-200" data-testid="ar-aging-card">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
              Receivables aging
            </div>
            <h2 className="text-base font-semibold tracking-tight">AR outstanding by bucket</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">
              Total open AR{" "}
              <span className="font-medium text-slate-900">
                {aging ? fmtMoney(aging.total) : "—"}
              </span>
            </p>
          </div>
          <Clock className="w-5 h-5 text-amber-500" strokeWidth={1.75} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          {(aging?.buckets || []).map((b, i) => {
            const palette = [
              { bg: "bg-emerald-50", bar: "bg-emerald-500", text: "text-emerald-700", ring: "ring-emerald-100" },
              { bg: "bg-sky-50", bar: "bg-sky-500", text: "text-sky-700", ring: "ring-sky-100" },
              { bg: "bg-blue-50", bar: "bg-blue-500", text: "text-blue-700", ring: "ring-blue-100" },
              { bg: "bg-amber-50", bar: "bg-amber-500", text: "text-amber-700", ring: "ring-amber-100" },
              { bg: "bg-orange-50", bar: "bg-orange-500", text: "text-orange-700", ring: "ring-orange-100" },
              { bg: "bg-rose-50", bar: "bg-rose-500", text: "text-rose-700", ring: "ring-rose-100" },
            ][i % 6];
            return (
              <button
                key={b.key}
                type="button"
                data-testid={`ar-bucket-${b.key}`}
                onClick={() => navigate(`/policies?ar_bucket=${b.key}`)}
                className={`${palette.bg} ring-1 ${palette.ring} rounded-lg p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm hover:ring-2 cursor-pointer`}
              >
                <div className={`text-[11px] uppercase tracking-wider font-medium ${palette.text}`}>
                  {b.label}
                </div>
                <div className="text-lg font-semibold tabular-nums text-slate-900 mt-1">
                  {fmtMoneyShort(b.value)}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{b.pct.toFixed(1)}%</div>
                <div className="mt-2 h-1.5 rounded-full bg-white/60 overflow-hidden">
                  <div
                    className={`h-full ${palette.bar}`}
                    style={{ width: `${Math.max(2, Math.min(100, b.pct))}%` }}
                  />
                </div>
              </button>
            );
          })}
          {!aging && (
            <div className="col-span-full text-sm text-slate-400 text-center py-4">Loading…</div>
          )}
        </div>

        {/* stacked bar */}
        {aging && aging.total > 0 && (
          <div>
            <div className="flex h-2.5 rounded-full overflow-hidden ring-1 ring-slate-200">
              {aging.buckets.map((b, i) => {
                const colors = ["#10B981", "#0EA5E9", "#3B82F6", "#F59E0B", "#F97316", "#F43F5E"];
                return (
                  <div
                    key={b.key}
                    className="h-full"
                    style={{ width: `${b.pct}%`, background: colors[i % colors.length] }}
                    title={`${b.label}: ${fmtMoney(b.value)}`}
                  />
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* Geographic concentration */}
      <StateMap />

      {/* Latest production (rich) */}
      <LatestProduction />

      {/* Recent table + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
                Latest production
              </div>
              <h2 className="text-base font-semibold tracking-tight">Recent policies on file</h2>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="py-2 pr-4 font-medium">Insured</th>
                  <th className="py-2 pr-4 font-medium">Carrier</th>
                  <th className="py-2 pr-4 font-medium">Segment</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">State</th>
                  <th className="py-2 pr-4 font-medium text-right">GWP</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-3 pr-4 text-slate-900 truncate max-w-[280px]" title={p.insured_name}>
                      {p.insured_name || "—"}
                    </td>
                    <td className="py-3 pr-4 text-slate-600">{p.carrier || "—"}</td>
                    <td className="py-3 pr-4 text-slate-600">{p.business_segment || "—"}</td>
                    <td className="py-3 pr-4">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-700">
                        {p.transaction_code || "—"}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-slate-600">{p.state || "—"}</td>
                    <td className="py-3 pr-4 text-right font-medium text-slate-900">{fmtMoney(p.reported_gwp)}</td>
                  </tr>
                ))}
                {recent.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-400 text-sm">
                      No policies yet — upload a file or wait for the seed to finish.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <ActivityFeed items={activity} onRefresh={refreshActivity} />
      </div>
    </div>
  );
}

// === Activity feed ===
const ACTIVITY_STYLES = {
  "policy:create": { icon: Plus, color: "text-emerald-700", bg: "bg-emerald-100", ring: "ring-emerald-200", label: "Policy created" },
  "policy:update": { icon: Pencil, color: "text-blue-700", bg: "bg-blue-100", ring: "ring-blue-200", label: "Policy updated" },
  "policy:delete": { icon: Trash2, color: "text-rose-700", bg: "bg-rose-100", ring: "ring-rose-200", label: "Policy deleted" },
  "journal_entry:create": { icon: Plus, color: "text-indigo-700", bg: "bg-indigo-100", ring: "ring-indigo-200", label: "JE posted" },
  "journal_entry:update": { icon: Pencil, color: "text-indigo-700", bg: "bg-indigo-100", ring: "ring-indigo-200", label: "JE edited" },
  "journal_entry:delete": { icon: Trash2, color: "text-rose-700", bg: "bg-rose-100", ring: "ring-rose-200", label: "JE deleted" },
  "upload:upload": { icon: UploadIcon, color: "text-amber-700", bg: "bg-amber-100", ring: "ring-amber-200", label: "File uploaded" },
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
}

function ActivityFeed({ items, onRefresh }) {
  return (
    <Card className="p-6 border-slate-200" data-testid="activity-feed">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
            Activity
          </div>
          <h2 className="text-base font-semibold tracking-tight">Recent changes</h2>
          <p className="text-[12px] text-slate-500 mt-0.5">
            Every create / update / delete across policies, JEs & uploads.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            data-testid="activity-refresh"
            onClick={onRefresh}
            className="text-[11px] text-slate-500 hover:text-slate-700"
          >
            Refresh
          </button>
          <a
            href="/activity"
            data-testid="activity-view-all"
            className="text-[11px] text-blue-600 hover:underline"
          >
            View all →
          </a>
        </div>
      </div>

      {items.length === 0 && (
        <div className="py-10 text-center">
          <ActivityIcon className="w-8 h-8 mx-auto text-slate-300" strokeWidth={1.5} />
          <div className="text-sm text-slate-500 mt-2">No activity yet.</div>
          <div className="text-[11px] text-slate-400 mt-1">
            As your team edits records, the feed lights up here.
          </div>
        </div>
      )}

      {items.length > 0 && (
        <ol
          className="space-y-4 max-h-[540px] overflow-y-auto overflow-x-hidden px-1"
          data-testid="activity-list"
        >
          {items.map((a, i) => {
            const key = `${a.entity}:${a.action}`;
            const s = ACTIVITY_STYLES[key] || {
              icon: ActivityIcon,
              color: "text-slate-700",
              bg: "bg-slate-100",
              ring: "ring-slate-200",
              label: key,
            };
            const Icon = s.icon;
            const isLast = i === items.length - 1;
            return (
              <li key={i} className="flex gap-3" data-testid={`activity-item-${i}`}>
                {/* icon + connector line */}
                <div className="flex flex-col items-center shrink-0 w-8">
                  <div
                    className={`w-8 h-8 rounded-full ${s.bg} ring-1 ${s.ring} flex items-center justify-center shrink-0`}
                  >
                    <Icon className={`w-4 h-4 ${s.color}`} strokeWidth={2.25} />
                  </div>
                  {!isLast && <div className="w-px flex-1 bg-slate-200 mt-1.5 mb-[-4px]" />}
                </div>

                {/* content */}
                <div className="flex-1 min-w-0 pb-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-[12px] font-medium text-slate-900 truncate" title={a.title}>
                      {a.title}
                    </div>
                    <time className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">
                      {relTime(a.at)}
                    </time>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${s.bg} ${s.color} font-medium`}
                    >
                      {s.label}
                    </span>
                    <span className="truncate">by {a.user_name || a.user_email}</span>
                  </div>
                  {a.meta?.fields?.length > 0 && (
                    <div className="mt-1 text-[11px] text-slate-500">
                      Fields: {a.meta.fields.slice(0, 4).join(", ")}
                      {a.meta.fields.length > 4 && ` +${a.meta.fields.length - 4} more`}
                    </div>
                  )}
                  {a.entity === "upload" && (
                    <div className="mt-1 text-[11px] text-slate-500">
                      +{(a.meta?.inserted ?? 0).toLocaleString()} new •{" "}
                      {(a.meta?.updated ?? 0).toLocaleString()} updated
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
